import { useEffect } from 'react';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { loadPendingSocialSignup } from '@/utils/pendingSocialSignup';
import { authBreadcrumb, authError } from '@/utils/authObservability';

/**
 * Completes a `signInWithRedirect` sign-in on app load, wherever the user lands.
 *
 * Redirect-based social auth (used in Safari / iOS-WebViews / in-app browsers,
 * where `signInWithPopup` is silently blocked) navigates the whole page away to
 * the provider and back. `getRedirectResult()` MUST be called once on return to
 * resolve the pending credential — otherwise the sign-in never completes.
 *
 * `TrialSignupFlow` already does this for the *signup* path, because it needs
 * the context it stashed before leaving (consent, exam type, attribution) to
 * create the trial. This hook covers everything else — chiefly a returning user
 * who tapped Google/Apple on the SignIn page inside a webview. Once the redirect
 * resolves, Firebase sets `currentUser`.
 *
 * Routing is NOT this hook's job (it runs globally at app mount, page-agnostic).
 * Completing the redirect only establishes the session; the page the user landed
 * on routes them onward from `currentUser`:
 *  - SignIn has its own `currentUser` → `/account-setup` effect.
 *  - The trial-signup page uses `useRedirectAuthenticatedUser`.
 * This matters for the bridge-lost case: when the `pendingSocialSignup` bridge
 * is dropped across the redirect (common in in-app browsers), this hook does NOT
 * cede — it completes the session — and the page guard then routes the user, so
 * they're never left authenticated-but-stranded on the signup page.
 *
 * Idempotent & non-clobbering:
 *  - `getRedirectResult()` returns null when nothing is pending → no-op.
 *  - It's a one-shot (Firebase clears the result after the first read), so this
 *    hook cedes to `TrialSignupFlow` whenever a pending signup exists (guarded
 *    via the `pendingSocialSignup` sessionStorage bridge) and never
 *    double-consumes the result. When the bridge survived, TrialSignupFlow owns
 *    both completion AND routing; when it was lost, this hook completes and the
 *    page guard routes.
 */
export function useSocialRedirectCompletion(): void {
  const { completeSocialRedirect } = useEnhancedAuth();

  useEffect(() => {
    // Build-time prerender (vite.config's puppeteer): there is no user and no auth
    // backend. Skip the async getRedirectResult — completing it triggers a late
    // re-render that makes react-helmet reconcile the route's <title> back to the
    // shell default, corrupting the prerendered <head>. Flag is injected before
    // any app script runs (evaluateOnNewDocument).
    if ((window as unknown as { __PRERENDER_INJECTED?: unknown }).__PRERENDER_INJECTED) return;

    // A trial signup is mid-redirect → TrialSignupFlow owns completion (it needs
    // the stashed context). Don't consume the one-shot result out from under it.
    if (loadPendingSocialSignup()) return;

    let cancelled = false;
    (async () => {
      try {
        const result = await completeSocialRedirect();
        if (cancelled) return;
        // Only breadcrumb when a redirect was actually initiated (marker set in
        // startSocialRedirect). Without this gate, getRedirectResult()'s null on
        // every normal page load logged 'signin-redirect-return' each time — 356
        // info items in Rollbar. With the marker, a null result now genuinely
        // means a DROPPED redirect (the signal we wanted), not a plain load.
        let initiated = false;
        try {
          initiated = sessionStorage.getItem('swtpa_redirect_initiated') === '1';
          if (initiated) sessionStorage.removeItem('swtpa_redirect_initiated');
        } catch { /* private mode */ }
        if (initiated) {
          authBreadcrumb('signin-redirect-return', { has_result: !!result?.user });
        }
        if (!result?.user) return; // no pending redirect → no-op
        // Session is established; onAuthStateChanged / currentUser drive routing.
        // Do not copy leftover homepage 6Q onto a returning account — that
        // persist belongs to first-run user-doc creation (popup / trial signup).
        authBreadcrumb('signin-redirect-completion-success', { uid: result.user.uid });
      } catch (error) {
        // A genuine redirect auth error (e.g. account-exists-with-different-
        // credential). Non-fatal for the user (stays signed out, can retry) but
        // it must NOT be swallowed — send a visible Rollbar item with the code.
        authError('signin-redirect-completion-failed', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [completeSocialRedirect]);
}
